/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { FunctionComponent, JSX } from "react";
import { useEffect, useRef } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";

import { LocationUrls } from "../../viewModel/navigation";
import { Log } from "../../viewModel/logging";
import {
  useUnityDispatch,
  useUnitySelector
} from "../../viewModel/UnityAppStore";
import {
  selectIsTokenRehydrating,
  selectIsAuthenticated,
  selectIsInitialized,
  selectIsAuthOrchestrationPending
} from "../../viewModel/signIn";
import {
  postSigninStartupSequence,
  selectHasStartedUp,
  selectIsProfileGateResolved,
  selectIsStarting,
  selectRequiresProfileSelection,
  selectStartupError
} from "../../viewModel/postSigninStartup";
import { LoadingIndicator } from "../common";

import "./ProtectedRoute.scss";

/**
 * Route guard for authenticated routes.
 *
 * While the async IndexedDB token read is in progress (isTokenRehydrating),
 * renders nothing to avoid a flash-redirect. Once rehydration completes,
 * authenticated users see child routes; unauthenticated users are
 * redirected to the sign-in screen with `state.from` preserved.
 *
 * Additionally ensures the post-sign-in startup sequence runs for
 * authenticated sessions that bypassed the SplashScreen/SignIn flow
 * (e.g. direct navigation to a protected route with a stored token).
 *
 * No child screen mounts until that sequence has finished: an authenticated
 * session with startup still in flight holds on a loading indicator so no
 * screen (profile, home, player) can fetch before its critical prerequisites
 * — device features carrying `msoServiceId`, profiles, channels — have landed.
 */
const ProtectedRoute: FunctionComponent = (): JSX.Element | null => {
  const TAG: string = "ProtectedRoute";

  const dispatch = useUnityDispatch();
  const isTokenRehydrating: boolean = useUnitySelector(
    selectIsTokenRehydrating
  );
  const isAuthenticated: boolean = useUnitySelector(selectIsAuthenticated);
  const isSignInInitialized: boolean = useUnitySelector(selectIsInitialized);
  const isAuthOrchestrationPending: boolean = useUnitySelector(
    selectIsAuthOrchestrationPending
  );
  const hasStartedUp: boolean = useUnitySelector(selectHasStartedUp);
  const isStarting: boolean = useUnitySelector(selectIsStarting);
  const isProfileGateResolved: boolean = useUnitySelector(
    selectIsProfileGateResolved
  );
  const requiresProfileSelection: boolean = useUnitySelector(
    selectRequiresProfileSelection
  );
  const startupError: string | null = useUnitySelector(selectStartupError);
  const location = useLocation();
  const hasDispatched = useRef<boolean>(false);
  const isProfileSelectionRoute: boolean =
    location.pathname === LocationUrls.SELECT_PROFILE;

  useEffect((): (() => void) => {
    if (
      isAuthenticated &&
      !hasStartedUp &&
      !isStarting &&
      !isProfileGateResolved &&
      !hasDispatched.current
    ) {
      hasDispatched.current = true;
      Log.d(
        TAG,
        "useEffect() authenticated without prior startup, dispatching postSigninStartupSequence"
      );
      dispatch(
        postSigninStartupSequence({
          forceProfileSelection: isProfileSelectionRoute,
          isRootEntry: false
        })
      );
    }
    return (): void => {
      // No cleanup required
    };
  }, [
    dispatch,
    isAuthenticated,
    hasStartedUp,
    isProfileGateResolved,
    isProfileSelectionRoute,
    isStarting
  ]);

  if (isTokenRehydrating) {
    return null;
  }

  // Wait for sign-in initialization and any silent auth flow (IP auto-login)
  // before deciding to redirect. Without this, a direct-load on a protected
  // URL would flash the sign-in screen while IP auto-login is still running.
  // Redirect to splash so the user sees branding logo and loading indicator.
  // `state.from` carries the requested URL so SplashScreen can land the user
  // back on it instead of on Home (a typed or reloaded deep link, UAW-1129).
  if (
    !isAuthenticated &&
    (!isSignInInitialized || isAuthOrchestrationPending)
  ) {
    Log.d(
      TAG,
      "redirecting to splash — isSignInInitialized=" +
        String(isSignInInitialized) +
        " isAuthOrchestrationPending=" +
        String(isAuthOrchestrationPending)
    );
    return (
      <Navigate to={LocationUrls.SPLASH} state={{ from: location }} replace />
    );
  }

  if (!isAuthenticated) {
    return (
      <Navigate to={LocationUrls.SIGN_IN} state={{ from: location }} replace />
    );
  }

  if (
    isProfileGateResolved &&
    requiresProfileSelection &&
    location.pathname !== LocationUrls.SELECT_PROFILE
  ) {
    return <Navigate to={LocationUrls.SELECT_PROFILE} replace />;
  }

  // Hold every authenticated screen until startup finishes, so no screen
  // mounts against half-loaded session data while the post-sign-in sequence
  // (device features, profiles, channels) is still running. On a startup error
  // fall through so the sequence's own recovery path takes over rather than
  // pinning the user here forever.
  //
  // The wait is shown, not blank. The longest one is the first profile
  // selection: picking a profile mints a profile-scoped domain token and then
  // resumes the sequence past its gate, and every one of those calls happens
  // with this guard holding the next screen back. SplashScreen owns the
  // equivalent wait at boot and shows branding plus its own loader, and it
  // sits outside this guard, so the two cannot both be on screen.
  const canRenderProfileSelection: boolean =
    isProfileSelectionRoute &&
    isProfileGateResolved &&
    requiresProfileSelection;
  if (!hasStartedUp && startupError === null && !canRenderProfileSelection) {
    return (
      <div
        className="protected-route-loader"
        data-testid="protected-route-loader"
        aria-hidden="true"
      >
        <LoadingIndicator alt="" />
      </div>
    );
  }

  return <Outlet />;
};

export default ProtectedRoute;
